import React from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';

// Layout components
import AuthLayout from '../layout/AuthLayout';
import MainLayout from '../layout/MainLayout';

// Common components
import ProtectedRoute from './ProtectedRoute';

// Page components
import Login from '../../pages/auth/Login';
import Signup from '../../pages/auth/Signup';
import BusinessProfile from '../../pages/auth/BusinessProfile';
import ForceLogout from '../../pages/auth/ForceLogout';
import Dashboard from '../../pages/dashboard/Dashboard';

// Debug components
import PaymentDebug from '../../components/debug/PaymentDebug';
import PaymentTest from '../../components/debug/PaymentTest';

// Feature pages
import ContactsList from '../../pages/contacts/ContactsList';
import ContactForm from '../../pages/contacts/ContactForm';
import ContactDetails from '../../pages/contacts/ContactDetails';

import InventoryList from '../../pages/inventory/InventoryList';
import InventoryForm from '../../pages/inventory/InventoryForm';
import InventoryDetails from '../../pages/inventory/InventoryDetails';

import PurchaseOrdersList from '../../pages/orders/PurchaseOrdersList';
import PurchaseOrderForm from '../../pages/orders/PurchaseOrderForm';
import PurchaseOrderDetails from '../../pages/orders/PurchaseOrderDetails';

import RepairOrdersList from '../../pages/orders/RepairOrdersList';
import RepairOrderForm from '../../pages/orders/RepairOrderForm';
import RepairOrderDetails from '../../pages/orders/RepairOrderDetails';
import PublicRepairOrderDetails from '../../pages/orders/PublicRepairOrderDetails';

import InvoicesList from '../../pages/invoices/InvoicesList';
import InvoiceForm from '../../pages/invoices/InvoiceForm';
import RepairInvoicesList from '../../pages/invoices/RepairInvoicesList';
import RepairInvoiceForm from '../../pages/invoices/RepairInvoiceForm';

import AccountsList from '../../pages/accounts/AccountsList';
import AccountForm from '../../pages/accounts/AccountForm';
import AccountDetails from '../../pages/accounts/AccountDetails';

import PaymentsList from '../../pages/payments/PaymentsList';
import PaymentForm from '../../pages/payments/PaymentForm';

import Reports from '../../pages/reports/Reports';

import Settings from '../../pages/settings/Settings';

// Layout wrappers that use Outlet
const AuthLayoutWrapper: React.FC = () => (
  <AuthLayout>
    <Outlet />
  </AuthLayout>
);

const MainLayoutWrapper: React.FC = () => (
  <ProtectedRoute requireBusiness>
    <MainLayout>
      <Outlet />
    </MainLayout>
  </ProtectedRoute>
);

const AppRouter: React.FC = () => {
  return (
    <Routes>
      {/* Authentication routes - prevent access if already authenticated */}
      <Route element={<AuthLayoutWrapper />}>
        <Route
          path="/login"
          element={
            <ProtectedRoute requireAuth={false}>
              <Login />
            </ProtectedRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <ProtectedRoute requireAuth={false}>
              <Signup />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Force logout route */}
      <Route path="/forcelogout" element={<ForceLogout />} />

      {/* Business profile creation - only accessible if authenticated but no business */}
      <Route
        path="/create-business"
        element={
          <ProtectedRoute requireAuth={true} requireBusiness={false}>
            <BusinessProfile />
          </ProtectedRoute>
        }
      />

      {/* Main application routes */}
      <Route element={<MainLayoutWrapper />}>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />

        {/* Contacts routes */}
        <Route path="/contacts" element={<ContactsList />} />
        <Route path="/contacts/new" element={<ContactForm />} />
        <Route path="/contacts/:id" element={<ContactDetails />} />
        <Route path="/contacts/:id/edit" element={<ContactForm />} />

        {/* Inventory routes */}
        <Route path="/inventory" element={<InventoryList />} />
        <Route path="/inventory/new" element={<InventoryForm />} />
        <Route path="/inventory/:id" element={<InventoryDetails />} />
        <Route path="/inventory/:id/edit" element={<InventoryForm />} />

        {/* Purchase Orders routes */}
        <Route path="/purchase-orders" element={<PurchaseOrdersList />} />
        <Route path="/purchase-orders/new" element={<PurchaseOrderForm />} />
        <Route path="/purchase-orders/:id" element={<PurchaseOrderDetails />} />
        <Route path="/purchase-orders/:id/edit" element={<PurchaseOrderForm />} />

        {/* Repair Orders routes */}
        <Route path="/repair-orders" element={<RepairOrdersList />} />
        <Route path="/repair-orders/new" element={<RepairOrderForm />} />
        <Route path="/repair-orders/:id" element={<RepairOrderDetails />} />
        <Route path="/repair-orders/:id/edit" element={<RepairOrderForm />} />

        {/* Repair Invoices routes */}
        <Route path="/repair-invoices" element={<RepairInvoicesList />} />
        <Route path="/repair-invoices/new" element={<RepairInvoiceForm />} />
        <Route path="/repair-invoices/:id/edit" element={<RepairInvoiceForm />} />

        {/* Invoices routes */}
        <Route path="/invoices" element={<InvoicesList />} />
        <Route path="/invoices/new" element={<InvoiceForm />} />
        <Route path="/invoices/:id/edit" element={<InvoiceForm />} />

        {/* Accounts routes */}
        <Route path="/accounts" element={<AccountsList />} />
        <Route path="/accounts/new" element={<AccountForm />} />
        <Route path="/accounts/:id" element={<AccountDetails />} />
        <Route path="/accounts/:id/edit" element={<AccountForm />} />

        {/* Payments routes */}
        <Route path="/payments" element={<PaymentsList />} />
        <Route path="/payments/new" element={<PaymentForm />} />
        <Route path="/payments/:id/edit" element={<PaymentForm />} />

        {/* Reports routes */}
        <Route path="/reports" element={<Reports />} />

        {/* Settings routes */}
        <Route path="/settings" element={<Settings />} />

        {/* Debug routes */}
        <Route path="/debug/payment" element={<PaymentDebug />} />
        <Route path="/debug/test" element={<PaymentTest />} />
      </Route>

      {/* Public routes - no authentication required */}
      <Route path="/public/repair-order/:id" element={<PublicRepairOrderDetails />} />

      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default AppRouter;